import type { FC } from 'react';
import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { Box, Stack } from '@atlaskit/primitives';
import Heading from '@atlaskit/heading';

import { ErrorDisplay } from '@confluence/error-boundary';
import { useSessionData } from '@confluence/session-data';

import { useUsersInfoQuery } from '../useUsersInfoQuery';

import { PremiumFeatureBoxComponent } from './PremiumFeatureBoxComponent';
import type { PremiumFeature, PremiumFeatureItem } from './PremiumFeatureTypes';

const i18n = defineMessages({
	modalTitle: {
		id: 'premium-trial.bite-sized.modal.title',
		defaultMessage: 'Get to know Confluence Premium, {name}!',
		description: 'Title for bite sized premium trial onboarding modal',
	},
	modalBodyText: {
		id: 'premium-trial.bite-sized.modal.body.text',
		defaultMessage:
			'You have {daysLeft, plural, one {# day} other {# days}} left to try these features.',
		description: 'Body text for bite sized premium trial onboarding modal',
	},
});

export type PremiumFeatureTastingGenericLeftPanelProps = {
	setFeatureShowing: React.Dispatch<React.SetStateAction<PremiumFeature>>;
	featureShowing: PremiumFeature;
	featureDetailsMap: PremiumFeatureItem[];
	trialDaysLeft: number;
	onFeatureLinkClick: (feature: PremiumFeature) => void;
};

export const PremiumFeatureTastingGenericLeftPanel: FC<
	PremiumFeatureTastingGenericLeftPanelProps
> = ({
	setFeatureShowing,
	featureShowing,
	featureDetailsMap,
	trialDaysLeft,
	onFeatureLinkClick,
}) => {
	const { userId } = useSessionData();

	const {
		usersInfo: currentUsersInfo,
		loading: currentUsersInfoLoading,
		error: usersInfoQueryError,
		isUsersInfoValid,
	} = useUsersInfoQuery({
		accountIds: userId != null ? [userId] : undefined,
	});

	if (usersInfoQueryError) {
		return <ErrorDisplay error={usersInfoQueryError} />;
	}

	if (currentUsersInfoLoading) {
		return null;
	}

	const userFullName = isUsersInfoValid ? currentUsersInfo[userId as string]?.displayName : '';

	return (
		<>
			<Stack space="space.100">
				<Heading size="large">
					<FormattedMessage {...i18n.modalTitle} values={{ name: userFullName }} />
				</Heading>
				<Box>
					<FormattedMessage
						{...i18n.modalBodyText}
						values={{
							daysLeft: trialDaysLeft,
						}}
					/>
				</Box>
			</Stack>
			<Stack space="space.100">
				{featureDetailsMap.map((featureItem) => (
					<PremiumFeatureBoxComponent
						key={featureItem.feature}
						featureShowing={featureShowing}
						setFeatureShowing={setFeatureShowing}
						premiumFeatureItem={featureItem}
						onFeatureLinkClick={onFeatureLinkClick}
					/>
				))}
			</Stack>
		</>
	);
};
